import { useCallback, useEffect, useState } from 'react'

import { api } from '../../lib/api'
import { API_BASE } from '../../config'
import { errorText } from '../../lib/offsite'
import { dayHourMin, hoursToHM } from '../../lib/personFormat'
import { dayLabel } from './parts'

/* ============================================================================
   อนุมัติของหัวหน้า — `/me/approvals`
   API: GET  /api/me/approvals
        POST /api/me/approvals/:rowId/act        (ทีละใบ)
        POST /api/me/approvals/act-many          (หลายใบพร้อมกัน)
        GET  /api/me/approvals/:rowId/evidence
        backend/src/routes/me.js → lib/subordinates.js + lib/approvals.actChain

   หัวหน้า (พนักงานธรรมดา ไม่ใช่ admin) เห็นเฉพาะใบของลูกน้องที่ "รอตัวเองอนุมัติ"
   ชั้นหัวหน้า 1/2 — ใบที่ผ่านชั้นเราแล้ววิ่งต่อไปหา HR เอง

   ── ทำไมเป็นตารางคอลัมน์ครบ + ติ๊กหลายใบ ───────────────────────────────────
   ของจริงหัวหน้าคนเดียวมีใบค้างหลายสิบใบ (วัดได้ 34 ใบ) การกดอนุมัติทีละใบ
   = 34 ครั้ง × ยืนยัน 1 ครั้ง · ตารางให้กวาดสายตาเทียบหลายใบพร้อมกันได้
   แล้วติ๊กอนุมัติเป็นชุด ส่วนรายละเอียดที่ต้องอ่าน (เหตุผล) อยู่ในคอลัมน์
   พร้อม title ให้ชี้ดูตัวเต็มเมื่อยาวเกิน
   ============================================================================ */

type ChainStep = { by: string | null; name: string | null; at: string | null; done: boolean }

type Form = {
  rowId: string
  empCode: string
  empName: string | null
  leaveType: string
  typeDesc: string
  isAdjust: boolean
  dayRows: number
  hours: number
  firstDay: string
  lastDay: string
  /** 'YYYY-MM-DD hh:mm' — เวลาจริง ดูได้ว่าครึ่งวันหรือเต็มวัน */
  startAt: string | null
  endAt: string | null
  reason: string | null
  hasEvidence: boolean
  level: number
  chain: { approve1: ChainStep; approve2: ChainStep }
  createdAt: string | null
}

type Kind = 'leave' | 'adjust'
/** นับปีจากวันที่ลา หรือวันที่ยื่นใบ */
type Basis = 'leave' | 'created'

type ActResult = { rowId: string; ok: boolean; error: string | null }

/** เวลาของใบ: ใบลาคิดเป็นวัน (1 วัน = 8 ชม.) · ใบปรับปรุงเวลาคิดเป็นชั่วโมง */
const spent = (hours: number, isAdjust: boolean) =>
  isAdjust ? hoursToHM(hours) : dayHourMin(hours / 8)

/** 'YYYY-MM-DD hh:mm' → 'hh:mm' (เอาไว้ต่อท้ายวันที่ ไม่ต้องซ้ำวัน) */
const timeOf = (s: string | null) => (s && s.length >= 16 ? s.slice(11, 16) : null)

/** เปิดไฟล์แนบในแท็บใหม่ — ต้อง fetch พร้อม cookie (endpoint อยู่หลัง auth) */
async function openEvidence(rowId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/me/approvals/${encodeURIComponent(rowId)}/evidence`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error(`เปิดไฟล์ไม่ได้ (HTTP ${res.status})`)
  const url = URL.createObjectURL(await res.blob())
  window.open(url, '_blank', 'noopener')
  // ปล่อยให้เบราว์เซอร์ถือ url ไว้สักพัก — revoke เร็วไปรูป/pdf จะโหลดไม่ขึ้น
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export default function MyApprovals() {
  const [kind, setKind] = useState<Kind>('leave')
  /** '' = ทุกปี — คิวงานไม่ควรซ่อนใบค้างโดยที่ผู้ใช้ไม่ได้สั่ง */
  const [year, setYear] = useState('')
  const [basis, setBasis] = useState<Basis>('leave')
  const [forms, setForms] = useState<Form[] | null>(null)
  const [years, setYears] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** เลขที่ใบที่ติ๊กไว้ — เก็บเป็น Set เพื่อ toggle เร็วและไม่ซ้ำ */
  const [picked, setPicked] = useState<Set<string>>(new Set())
  /** ผลรายใบของการทำเป็นชุด (โชว์เฉพาะใบที่ล้ม) */
  const [failed, setFailed] = useState<ActResult[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const qs = new URLSearchParams({ kind, basis })
      if (year) qs.set('year', year)
      const d = await api<{ forms: Form[]; years: number[] }>(`/api/me/approvals?${qs}`)
      setForms(d.forms)
      setYears(d.years)
      setError(null)
    } catch (err) {
      setError(errorText(err))
      setForms(null)
    } finally {
      setLoading(false)
    }
  }, [kind, year, basis])

  useEffect(() => {
    void load()
  }, [load])

  // เปลี่ยนตัวกรอง = คนละชุดใบ ของที่ติ๊กไว้ใช้ต่อไม่ได้
  useEffect(() => {
    setPicked(new Set())
    setFailed([])
  }, [kind, year, basis])

  const list = forms ?? []
  const allPicked = list.length > 0 && list.every((f) => picked.has(f.rowId))

  /* รวมเวลาของใบที่ติ๊ก — ให้เห็นว่ากำลังจะอนุมัติสิทธิไปเท่าไร
     ไม่ห่อ useMemo: list เป็น array ใหม่ทุก render (forms ?? []) memo จึงไม่ช่วยอะไร
     และงานนี้คือ filter+reduce หลักสิบรายการ ถูกกว่าการจัดการ dependency ให้ถูก */
  const pickedTotal = list
    .filter((f) => picked.has(f.rowId))
    .reduce((sum, f) => sum + (f.hours || 0), 0)

  function toggle(rowId: string) {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(rowId)) next.delete(rowId)
      else next.add(rowId)
      return next
    })
  }

  function toggleAll() {
    setPicked(allPicked ? new Set() : new Set(list.map((f) => f.rowId)))
  }

  /** ถามเหตุผลเมื่อไม่อนุมัติ — คืน undefined = ผู้ใช้กดยกเลิก */
  function askNote(action: 'APPROVE' | 'REJECT', what: string): string | null | undefined {
    if (action === 'REJECT') {
      const reason = window.prompt(`เหตุผลที่ไม่อนุมัติ${what} (จะบันทึกไว้):`)
      if (reason === null) return undefined
      return reason.trim() || null
    }
    return window.confirm(`อนุมัติ${what}?`) ? null : undefined
  }

  /** ทีละใบ */
  async function actOne(rowId: string, action: 'APPROVE' | 'REJECT') {
    const note = askNote(action, `ใบ ${rowId}`)
    if (note === undefined) return
    setBusy(true)
    setNotice(null)
    setError(null)
    setFailed([])
    try {
      await api(`/api/me/approvals/${encodeURIComponent(rowId)}/act`, {
        method: 'POST',
        body: JSON.stringify({ action, note: note ?? undefined }),
      })
      setNotice(action === 'APPROVE' ? `อนุมัติใบ ${rowId} แล้ว` : `ไม่อนุมัติใบ ${rowId} แล้ว`)
      setPicked((p) => {
        const n = new Set(p)
        n.delete(rowId)
        return n
      })
      await load()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  /** หลายใบพร้อมกัน */
  async function actMany(action: 'APPROVE' | 'REJECT') {
    const rowIds = [...picked]
    if (rowIds.length === 0) return
    const note = askNote(action, `${rowIds.length} ใบที่เลือก`)
    if (note === undefined) return

    setBusy(true)
    setNotice(null)
    setError(null)
    setFailed([])
    try {
      const r = await api<{ done: number; total: number; results: ActResult[] }>(
        '/api/me/approvals/act-many',
        { method: 'POST', body: JSON.stringify({ rowIds, action, note: note ?? undefined }) },
      )
      const label = action === 'APPROVE' ? 'อนุมัติ' : 'ไม่อนุมัติ'
      setNotice(`${label}แล้ว ${r.done} จาก ${r.total} ใบ`)
      // ใบที่ล้มต้องบอกว่าใบไหนและเพราะอะไร ไม่ใช่กลบด้วยยอดรวม
      setFailed(r.results.filter((x) => !x.ok))
      setPicked(new Set())
      await load()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h1>คำขอที่รอฉันอนุมัติ</h1>
      <p className="meta">
        ใบลา / ใบปรับปรุงเวลาของลูกน้องในสายของคุณ ที่รอคุณอนุมัติ (ชั้นหัวหน้า) —
        ผ่านคุณแล้วใบจะวิ่งต่อไปหา HR เอง · ติ๊กหลายใบเพื่ออนุมัติพร้อมกันได้
      </p>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      {failed.length > 0 && (
        <div className="warn">
          <p>
            <strong>{failed.length} ใบไม่สำเร็จ</strong> — ที่เหลือทำไปแล้ว
          </p>
          <ul className="todo-list">
            {failed.map((f) => (
              <li key={f.rowId}>
                <span>{f.rowId}</span>
                <span className="meta">{f.error ?? 'ไม่ทราบสาเหตุ'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <section className="card">
        <div className="tabs" role="tablist">
          {(['leave', 'adjust'] as Kind[]).map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={kind === k}
              className={kind === k ? 'tab tab--on' : 'tab'}
              onClick={() => setKind(k)}
            >
              {k === 'leave' ? 'ใบลา' : 'ใบปรับปรุงเวลา'}
            </button>
          ))}
          <button type="button" className="ghost" onClick={() => void load()} disabled={loading}>
            {loading ? 'กำลังโหลด…' : 'โหลดใหม่'}
          </button>
        </div>

        {/* ตัวกรองปี — ของจริงมีใบค้างข้ามปี (เจอใบปี 2023 ยังรออนุมัติอยู่)
            เกณฑ์เลือกได้ว่านับปีจาก "วันที่ลา" หรือ "วันที่ยื่น" เพราะสองอย่างนี้
            ต่างกันได้มาก (ยื่นปลายปีเพื่อลาต้นปีถัดไป) */}
        <div className="toolbar">
          <label>
            นับปีจาก
            <select value={basis} onChange={(e) => setBasis(e.target.value as Basis)}>
              <option value="leave">วันที่ลา</option>
              <option value="created">วันที่ยื่น</option>
            </select>
          </label>
          <label>
            ปี
            <select value={year} onChange={(e) => setYear(e.target.value)}>
              <option value="">ทุกปี</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          {year && (
            <button type="button" className="ghost" onClick={() => setYear('')}>
              ล้างตัวกรอง
            </button>
          )}
        </div>

        {/* แถบทำเป็นชุด — โผล่เมื่อติ๊กแล้ว ไม่กินที่ตอนไม่ได้ใช้ */}
        {picked.size > 0 && (
          <div className="toolbar">
            <span>
              เลือกไว้ <strong>{picked.size}</strong> ใบ
              <span className="meta">
                {' '}
                · รวม {spent(pickedTotal, kind === 'adjust')}
              </span>
            </span>
            <button type="button" onClick={() => void actMany('APPROVE')} disabled={busy}>
              {busy ? 'กำลังบันทึก…' : `อนุมัติ ${picked.size} ใบ`}
            </button>
            <button
              type="button"
              className="danger"
              onClick={() => void actMany('REJECT')}
              disabled={busy}
            >
              ไม่อนุมัติ {picked.size} ใบ
            </button>
            <button type="button" className="ghost" onClick={() => setPicked(new Set())} disabled={busy}>
              ล้างที่เลือก
            </button>
          </div>
        )}

        {forms === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : list.length === 0 ? (
          <p className="meta">ไม่มีใบที่รอคุณอนุมัติ</p>
        ) : (
          <>
            <p className="meta">{list.length} ใบ</p>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allPicked}
                        onChange={toggleAll}
                        aria-label="เลือกทั้งหมด"
                      />
                    </th>
                    <th>พนักงาน</th>
                    <th>เลขที่ใบ</th>
                    <th>ประเภท</th>
                    <th className="nowrap">ช่วงวัน</th>
                    <th className="nowrap">เวลา</th>
                    <th className="num">{kind === 'adjust' ? 'ชั่วโมง' : 'สิทธิที่ใช้'}</th>
                    <th>เหตุผล</th>
                    <th>เอกสาร</th>
                    <th>ผ่านอนุมัติแล้ว</th>
                    <th>ชั้น</th>
                    <th className="nowrap">ยื่นเมื่อ</th>
                    <th>จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((f) => {
                    const sameDay = f.firstDay === f.lastDay
                    /* ผู้อนุมัติก่อนหน้า = ชั้นที่กดผ่านไปแล้วก่อนถึงคิวเรา
                       ใบที่รอชั้น 1 จะว่าง · ใบที่รอชั้น 2 จะมีชั้น 1 ที่อนุมัติแล้ว */
                    const prior = [f.chain.approve1, f.chain.approve2].filter(
                      (s) => s.by && s.done,
                    )
                    const t1 = timeOf(f.startAt)
                    const t2 = timeOf(f.endAt)
                    return (
                      <tr key={f.rowId} className={picked.has(f.rowId) ? 'is-picked' : undefined}>
                        <td>
                          <input
                            type="checkbox"
                            checked={picked.has(f.rowId)}
                            onChange={() => toggle(f.rowId)}
                            aria-label={`เลือกใบ ${f.rowId}`}
                          />
                        </td>
                        <td>
                          <strong>{f.empName ?? '-'}</strong>
                          <div className="meta">{f.empCode}</div>
                        </td>
                        <td className="nowrap">{f.rowId}</td>
                        <td className="cell-clip" title={f.typeDesc}>
                          {f.typeDesc}
                        </td>
                        <td className="nowrap">
                          {sameDay ? dayLabel(f.firstDay) : `${dayLabel(f.firstDay)} – ${dayLabel(f.lastDay)}`}
                          {f.dayRows > 1 && <div className="meta">{f.dayRows} วัน</div>}
                        </td>
                        {/* เวลาจริง — บอกได้ว่าครึ่งวันหรือเต็มวัน */}
                        <td className="nowrap meta">{t1 && t2 ? `${t1}–${t2}` : '—'}</td>
                        <td className="num nowrap">
                          <strong>{f.hours ? spent(f.hours, f.isAdjust) : '—'}</strong>
                        </td>
                        {/* ตัดด้วย … แต่ชี้ดูตัวเต็มได้ (เหตุผลบางใบยาวเป็นย่อหน้า) */}
                        <td className="cell-clip" title={f.reason ?? ''}>
                          {f.reason || <span className="meta">—</span>}
                        </td>
                        <td>
                          {f.hasEvidence ? (
                            <button
                              type="button"
                              className="ghost tiny"
                              onClick={() => {
                                openEvidence(f.rowId).catch((e: unknown) => setError(errorText(e)))
                              }}
                            >
                              ดู
                            </button>
                          ) : (
                            <span className="meta">—</span>
                          )}
                        </td>
                        <td className="cell-clip">
                          {prior.length === 0 ? (
                            <span className="meta">—</span>
                          ) : (
                            prior.map((s) => (
                              <div key={s.by} title={s.at ?? ''}>
                                {s.name ?? s.by}
                              </div>
                            ))
                          )}
                        </td>
                        <td className="nowrap meta">ชั้น {f.level}</td>
                        <td className="nowrap meta">
                          {f.createdAt ? dayLabel(f.createdAt.slice(0, 10)) : '—'}
                        </td>
                        <td className="nowrap">
                          <button
                            type="button"
                            className="tiny"
                            onClick={() => void actOne(f.rowId, 'APPROVE')}
                            disabled={busy}
                          >
                            อนุมัติ
                          </button>{' '}
                          <button
                            type="button"
                            className="danger tiny"
                            onClick={() => void actOne(f.rowId, 'REJECT')}
                            disabled={busy}
                          >
                            ไม่อนุมัติ
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </>
  )
}
